/**
 * MUI theme factory - produces a complete MUI theme from semantic tokens.
 * Augments `Theme` with a `.tokens` property for direct access.
 */

import { createTheme, type Shadows } from '@mui/material/styles';
import { lightTokens, darkTokens, type SemanticTokens } from './tokens';
import { typography, radius, shadow } from './scales';

declare module '@mui/material/styles' {
  interface Theme {
    tokens: SemanticTokens;
  }
  interface ThemeOptions {
    tokens?: SemanticTokens;
  }
}

export const createAppTheme = (mode: 'light' | 'dark') => {
  const tokens = mode === 'light' ? lightTokens : darkTokens;

  return createTheme({
    tokens,
    palette: {
      mode,
      primary: { main: tokens.accent, contrastText: tokens.fgOnAccent },
      background: { default: tokens.bg, paper: tokens.surface },
      text: {
        primary: tokens.fgPrimary,
        secondary: tokens.fgSecondary,
        disabled: tokens.fgTertiary,
      },
      divider: tokens.border,
      success: { main: tokens.success },
      warning: { main: tokens.warning },
      error: { main: tokens.danger },
      info: { main: tokens.info },
    },
    typography: {
      fontFamily: typography.fontFamily.sans,
      fontSize: typography.size.base,
      h1: {
        fontWeight: typography.weight.bold,
        fontSize: typography.size['4xl'],
        lineHeight: typography.lineHeight.tight,
      },
      h2: {
        fontWeight: typography.weight.bold,
        fontSize: typography.size['3xl'],
        lineHeight: typography.lineHeight.tight,
      },
      h3: {
        fontWeight: typography.weight.bold,
        fontSize: typography.size['2xl'],
        lineHeight: typography.lineHeight.snug,
      },
      h4: {
        fontWeight: typography.weight.semibold,
        fontSize: typography.size.xl,
        lineHeight: typography.lineHeight.snug,
      },
      h5: {
        fontWeight: typography.weight.semibold,
        fontSize: typography.size.lg,
        lineHeight: typography.lineHeight.normal,
      },
      h6: {
        fontWeight: typography.weight.semibold,
        fontSize: typography.size.md,
        lineHeight: typography.lineHeight.normal,
      },
      body1: { fontSize: typography.size.base, lineHeight: typography.lineHeight.normal },
      body2: { fontSize: typography.size.sm, lineHeight: typography.lineHeight.normal },
      caption: { fontSize: typography.size.xs, lineHeight: typography.lineHeight.normal },
      button: {
        fontWeight: typography.weight.medium,
        fontSize: typography.size.base,
        textTransform: 'none' as const,
      },
    },
    shape: { borderRadius: radius.md },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          '*, *::before, *::after': {
            transition: 'background-color 180ms ease, border-color 180ms ease, color 180ms ease',
          },
          '@media (prefers-reduced-motion: reduce)': {
            '*, *::before, *::after': { transition: 'none' },
          },
          '[data-no-transition], [data-no-transition] *': { transition: 'none !important' },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { borderRadius: radius.sm, fontWeight: typography.weight.medium },
        },
      },
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: { backgroundImage: 'none', boxSizing: 'border-box' as const },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            fontSize: typography.size.sm,
            fontWeight: typography.weight.medium,
            backgroundColor: tokens.fgPrimary,
            color: tokens.surface,
            borderRadius: radius.sm,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: radius.lg,
            border: `1px solid ${tokens.border}`,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: radius.sm },
        },
      },
      MuiTableContainer: {
        styleOverrides: {
          root: { backgroundColor: tokens.surface },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            color: tokens.fgPrimary,
            borderBottomColor: tokens.border,
          },
        },
      },
      MuiTable: {
        styleOverrides: {
          root: { backgroundColor: tokens.surface },
        },
      },
      MuiTablePagination: {
        styleOverrides: {
          root: { color: tokens.fgSecondary },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: { '& .MuiOutlinedInput-root': { color: tokens.fgPrimary } },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            color: tokens.fgPrimary,
            '& .MuiOutlinedInput-notchedOutline': { borderColor: tokens.border },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: tokens.borderStrong },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: { color: tokens.fgSecondary },
        },
      },
      MuiSelect: {
        styleOverrides: {
          icon: { color: tokens.fgSecondary },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundColor: tokens.surface,
            color: tokens.fgPrimary,
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: { color: tokens.fgPrimary },
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: { color: tokens.fgPrimary },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            minHeight: 36,
            padding: '2px 12px',
            fontSize: 13,
            color: tokens.fgSecondary,
            '&.Mui-selected': { color: tokens.accent },
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: { minHeight: 36 },
          indicator: { backgroundColor: tokens.accent },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: { color: tokens.fgSecondary },
        },
      },
    },
    shadows: [
      shadow.none,
      shadow.sm,
      shadow.sm,
      shadow.md,
      shadow.md,
      shadow.md,
      shadow.lg,
      shadow.lg,
      shadow.lg,
      shadow.lg,
      shadow.lg,
      shadow.lg,
      shadow.lg,
      shadow.lg,
      shadow.lg,
      shadow.lg,
      shadow.lg,
      shadow.lg,
      shadow.lg,
      shadow.lg,
      shadow.lg,
      shadow.lg,
      shadow.lg,
      shadow.lg,
      shadow.lg,
    ] as Shadows,
  });
};
